import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import OrgaoModal from './components/modals/OrgaoModal';
import OrgaoDepartamentoModal from './components/modals/OrgaoDepartamentoModal';
import OrgaoFuncionarioModal from './components/modals/OrgaoFuncionarioModal';
import OrgaoDepartamentoListModal from './components/modals/OrgaoDepartamentoListModal';
import OrgaoFuncionarioListModal from './components/modals/OrgaoFuncionarioListModal';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import { formatCNPJ } from './utils/cnpjUtils';

function Orgaos() {
  const [orgaos, setOrgaos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [orgaoSelecionado, setOrgaoSelecionado] = useState(null);
  const [showOrgaoModal, setShowOrgaoModal] = useState(false);
  const [editingOrgao, setEditingOrgao] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  const [departamentos, setDepartamentos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [showDepartamentoModal, setShowDepartamentoModal] = useState(false);
  const [editingDepartamento, setEditingDepartamento] = useState(null);
  const [showFuncionarioModal, setShowFuncionarioModal] = useState(false);
  const [editingFuncionario, setEditingFuncionario] = useState(null);
  const [showDepartamentoListModal, setShowDepartamentoListModal] = useState(false);
  const [showFuncionarioListModal, setShowFuncionarioListModal] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ORGAO}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setOrgaos(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter órgãos:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, navigate]);

  useEffect(() => {
    if (orgaoSelecionado) {
      axios
        .get(`${API_BASE_URL.ORGAO_DEPARTAMENTO}/orgao/${orgaoSelecionado.idOrgao}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(res => setDepartamentos(res.data))
        .catch(() => setDepartamentos([]));

      axios
        .get(`${API_BASE_URL.ORGAO_FUNCIONARIO}/orgao/${orgaoSelecionado.idOrgao}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(res => setFuncionarios(res.data))
        .catch(() => setFuncionarios([]));
    } else {
      setDepartamentos([]);
      setFuncionarios([]);
    }
  }, [orgaoSelecionado, atualizar, token]);

  function handleDelete(c) {
    showConfirmModal('delete', () => handleConfirmDelete(c));
  }

  function handleConfirmDelete(c) {
    axios
      .delete(`${API_BASE_URL.ORGAO}/delete/` + c.idOrgao, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setAtualizar(prev => !prev);
        setOrgaoSelecionado(null);
      })
      .catch(error => {
        console.error('Erro ao excluir órgão:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
      });
  }

  function handleDeleteDepartamento(dep) {
    showConfirmModal('delete', () => {
      axios
        .delete(`${API_BASE_URL.ORGAO_DEPARTAMENTO}/delete/` + dep.idDepartamento, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => setAtualizar(prev => !prev))
        .catch(() => showDefaultModal('error', 'Erro ao excluir departamento!'));
    });
  }

  function handleDeleteFuncionario(func) {
    showConfirmModal('delete', () => {
      axios
        .delete(`${API_BASE_URL.ORGAO_FUNCIONARIO}/delete/` + func.idFuncionario, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => setAtualizar(prev => !prev))
        .catch(() => showDefaultModal('error', 'Erro ao excluir funcionário!'));
    });
  }

  const normalizeString = str => {
    return (str || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const orgaosFiltrados = Array.isArray(orgaos)
    ? orgaos.filter(c => {
        const normalizedBusca = normalizeString(filtro);
        return (
          normalizeString(c.nome || '').includes(normalizedBusca) ||
          normalizeString(c.cnpj || '').includes(normalizedBusca) ||
          normalizeString(c.logrCidade || '').includes(normalizedBusca) ||
          normalizeString(c.logrUf || '').includes(normalizedBusca)
        );
      })
    : [];

  const totalItems = orgaosFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedOrgaos = orgaosFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Órgãos Cadastrados" icon="bi-building" />
        <PageContent>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-success btn-sm"
                title="Cadastrar novo órgão"
                onClick={() => {
                  setEditingOrgao(null);
                  setShowOrgaoModal(true);
                }}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Novo
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={!orgaoSelecionado}
                onClick={() => {
                  if (orgaoSelecionado) {
                    setEditingOrgao(orgaoSelecionado);
                    setShowOrgaoModal(true);
                  }
                }}
                title="Editar órgão selecionado"
              >
                <i className="bi bi-pencil me-1"></i>
                Editar
              </button>
              <button
                className="btn btn-danger btn-sm"
                disabled={!orgaoSelecionado}
                onClick={() => handleDelete(orgaoSelecionado)}
                title="Excluir órgão selecionado"
              >
                <i className="bi bi-trash me-1"></i>
                Excluir
              </button>
              {orgaoSelecionado && (
                <>
                  <button
                    className="btn btn-info btn-sm text-white"
                    onClick={() => setShowDepartamentoListModal(true)}
                    title="Departamentos"
                  >
                    <i className="bi bi-diagram-3 me-1"></i>
                    Departamentos ({departamentos.length})
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowFuncionarioListModal(true)}
                    title="Funcionários"
                  >
                    <i className="bi bi-people me-1"></i>
                    Funcionários ({funcionarios.length})
                  </button>
                </>
              )}
            </div>
            <div className="col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar por CNPJ, Nome ou Cidade"
                  value={filtro}
                  onChange={e => setFiltro(e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
            <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="px-2 d-none d-md-table-cell">CNPJ</th>
                  <th scope="col" className="px-2">Nome</th>
                  <th scope="col" className="px-2">Cidade</th>
                  <th scope="col" className="px-2">UF</th>
                  <th scope="col" className="px-2">Situação</th>
                </tr>}>
              <tbody>
                {!isLoading &&
                  selectedOrgaos.map(c => (
                    <tr
                      key={c.idOrgao}
                      className={
                        orgaoSelecionado && orgaoSelecionado.idOrgao === c.idOrgao
                          ? 'table-primary'
                          : ''
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (orgaoSelecionado && orgaoSelecionado.idOrgao === c.idOrgao) {
                          setOrgaoSelecionado(null);
                        } else {
                          setOrgaoSelecionado(c);
                        }
                      }}
                    >
                      <td className="px-2 d-none d-md-table-cell">{formatCNPJ(c.cnpj)}</td>
                      <td className="px-2">{c.nome}</td>
                      <td className="px-2">{c.logrCidade || '-'}</td>
                      <td className="px-2">{c.logrUf || '-'}</td>
                      <td className="px-2">
                        <span className={`badge ${c.situacao === 'ATIVO' ? 'bg-success' : 'bg-danger'}`}>
                          {c.situacao}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </DataTable>
          </TableContainer>

          <ConfirmModal {...setConfirmModal} />
          <DefaultModal {...setDefaultModal} />

          <OrgaoModal
            show={showOrgaoModal}
            onClose={() => {
              setShowOrgaoModal(false);
              setEditingOrgao(null);
            }}
            onSuccess={() => {
              setShowOrgaoModal(false);
              setEditingOrgao(null);
              setAtualizar(prev => !prev);
            }}
            orgao={editingOrgao}
          />

          <OrgaoDepartamentoModal
            show={showDepartamentoModal}
            onClose={() => {
              setShowDepartamentoModal(false);
              setEditingDepartamento(null);
            }}
            onSuccess={() => {
              setShowDepartamentoModal(false);
              setEditingDepartamento(null);
              setAtualizar(prev => !prev);
            }}
            departamento={editingDepartamento}
            orgaoId={orgaoSelecionado?.idOrgao}
          />

          <OrgaoFuncionarioModal
            show={showFuncionarioModal}
            onClose={() => {
              setShowFuncionarioModal(false);
              setEditingFuncionario(null);
            }}
            onSuccess={() => {
              setShowFuncionarioModal(false);
              setEditingFuncionario(null);
              setAtualizar(prev => !prev);
            }}
            funcionario={editingFuncionario}
            orgaoId={orgaoSelecionado?.idOrgao}
            departamentos={departamentos}
          />

          <OrgaoDepartamentoListModal
            show={showDepartamentoListModal}
            onClose={() => setShowDepartamentoListModal(false)}
            orgao={orgaoSelecionado}
            onEdit={dep => {
              setShowDepartamentoListModal(false);
              setEditingDepartamento(dep);
              setShowDepartamentoModal(true);
            }}
            onDelete={dep => {
              setShowDepartamentoListModal(false);
              handleDeleteDepartamento(dep);
            }}
            onNovo={() => {
              setShowDepartamentoListModal(false);
              setEditingDepartamento(null);
              setShowDepartamentoModal(true);
            }}
          />

          <OrgaoFuncionarioListModal
            show={showFuncionarioListModal}
            onClose={() => setShowFuncionarioListModal(false)}
            orgao={orgaoSelecionado}
            departamentos={departamentos}
            onEdit={func => {
              setShowFuncionarioListModal(false);
              setEditingFuncionario(func);
              setShowFuncionarioModal(true);
            }}
            onDelete={func => {
              setShowFuncionarioListModal(false);
              handleDeleteFuncionario(func);
            }}
            onNovo={() => {
              setShowFuncionarioListModal(false);
              setEditingFuncionario(null);
              setShowFuncionarioModal(true);
            }}
          />
        </PageContent>
        <PaginationBar setPagination={setPagination}/>
      </ResponsiveShadow>
    </Layout>
  );
}

export default Orgaos;
